// features/super-admin/pages/ErrorLogDetailPage.tsx
/**
 * SUPER ADMIN — ERROR LOG DETAIL
 * Comprehensive view of a single error with stack trace and request context.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Server, XCircle, AlertTriangle, AlertCircle,
    Info, Clock, Globe, Monitor, Database, User,
    Building2, Tag, FileText, RefreshCw, Copy,
    ChevronRight, Braces, Route,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface ErrorLogDetail {
    id: string;
    tenantId: string | null;
    tenantName?: string;
    userId: string | null;
    userName?: string;
    errorCode: string | null;
    errorType: string;
    severity: string;
    message: string;
    stackTrace: string | null;
    requestMethod: string | null;
    requestUrl: string | null;
    requestPath: string | null;
    requestQuery: Record<string, any> | null;
    requestBody: Record<string, any> | null;
    requestHeaders: Record<string, any> | null;
    responseStatus: number | null;
    ipAddress: string | null;
    userAgent: string | null;
    durationMs: number | null;
    createdAt: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const SEVERITY_CONFIG: Record<string, { variant: 'danger' | 'warning' | 'info' | 'neutral'; icon: React.ElementType; label: string; color: string; bg: string }> = {
    critical: { variant: 'danger', icon: XCircle, label: 'Critical', color: 'text-rose-600', bg: 'bg-rose-50' },
    error: { variant: 'warning', icon: AlertTriangle, label: 'Error', color: 'text-amber-600', bg: 'bg-amber-50' },
    warning: { variant: 'info', icon: AlertCircle, label: 'Warning', color: 'text-blue-600', bg: 'bg-blue-50' },
    info: { variant: 'neutral', icon: Info, label: 'Info', color: 'text-slate-600', bg: 'bg-slate-50' },
};

const ERROR_TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string; bg: string }> = {
    validation: { label: 'Validation', icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
    unauthorized: { label: 'Unauthorized', icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
    forbidden: { label: 'Forbidden', icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50' },
    not_found: { label: 'Not Found', icon: Info, color: 'text-slate-600', bg: 'bg-slate-50' },
    conflict: { label: 'Conflict', icon: AlertTriangle, color: 'text-purple-600', bg: 'bg-purple-50' },
    server_error: { label: 'Server Error', icon: Server, color: 'text-red-600', bg: 'bg-red-50' },
    database_error: { label: 'Database Error', icon: Database, color: 'text-pink-600', bg: 'bg-pink-50' },
    unknown: { label: 'Unknown', icon: Info, color: 'text-slate-600', bg: 'bg-slate-50' },
};

// ============================================================
// FORMATTERS
// ============================================================

const formatDateTime = (date: string) => {
    try { return new Date(date).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return 'N/A'; }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ErrorLogDetailPage() {
    const { logId } = useParams<{ logId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);

    const { data: log, isLoading, isError, refetch } = useQuery<ErrorLogDetail>({
        queryKey: ['super-admin-error-log', logId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/error-logs/${logId}`);
            return data?.data;
        },
        enabled: !!logId && !!user,
    });

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            // Could add toast here
        });
    };

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-2xl border-4 border-rose-200 border-t-rose-600" />
        </div>
    );

    if (isError || !log) return (
        <div className="min-h-screen flex items-center justify-center">
            <ErrorState title="Error log not found" message="This error log doesn't exist or has been deleted." onBack={() => navigate('/super-admin/error-logs')} />
        </div>
    );

    const severityConfig = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
    const SeverityIcon = severityConfig.icon;
    const typeConfig = ERROR_TYPE_CONFIG[log.errorType] || ERROR_TYPE_CONFIG.unknown;
    const TypeIcon = typeConfig.icon;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30">
            <div className="max-w-[1400px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Back */}
                <button onClick={() => navigate('/super-admin/error-logs')}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-rose-600 group">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Error Logs
                </button>

                {/* ========== HERO CARD ========== */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-white rounded-3xl shadow-xl border-2 border-rose-200">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-rose-500 via-red-600 to-pink-600" />
                    <div className="p-8 lg:p-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-5">
                                <div className={clsx('w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl', severityConfig.bg)}>
                                    <SeverityIcon size={28} className={severityConfig.color} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                                        <Badge variant={severityConfig.variant} size="md">
                                            <SeverityIcon size={10} className="mr-1" /> {severityConfig.label}
                                        </Badge>
                                        <Badge variant="info" size="md">
                                            <TypeIcon size={10} className="mr-1" /> {typeConfig.label}
                                        </Badge>
                                        {log.responseStatus && (
                                            <Badge variant="danger" size="md">
                                                HTTP {log.responseStatus}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-lg lg:text-xl font-bold text-slate-900 mt-2">
                                        {log.message}
                                    </p>
                                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-2 flex-wrap">
                                        <span className="flex items-center gap-1.5">
                                            <Clock size={14} /> {formatDateTime(log.createdAt)}
                                        </span>
                                        {log.errorCode && (
                                            <span className="flex items-center gap-1.5">
                                                <Tag size={14} /> {log.errorCode}
                                            </span>
                                        )}
                                        {log.durationMs !== null && (
                                            <span className="flex items-center gap-1.5">
                                                <Clock size={14} /> {log.durationMs}ms
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => refetch()}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium shadow-sm">
                                <RefreshCw size={14} /> Refresh
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* ========== DETAIL GRID ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* LEFT: Stack Trace (2/3 width) */}
                    <div className="lg:col-span-2 space-y-5">
                        {/* Stack Trace */}
                        {log.stackTrace && (
                            <Card>
                                <CardHeader gradient>
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                            <Server size={18} className="text-rose-500" /> Stack Trace
                                        </h3>
                                        <button onClick={() => handleCopy(log.stackTrace!, 'Stack trace')}
                                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
                                            <Copy size={12} /> Copy
                                        </button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <pre className="text-xs font-mono text-rose-700 whitespace-pre-wrap bg-rose-50/50 rounded-xl p-4 max-h-[400px] overflow-y-auto">
                                        {log.stackTrace}
                                    </pre>
                                </CardContent>
                            </Card>
                        )}

                        {/* Request Body */}
                        {log.requestBody && Object.keys(log.requestBody).length > 0 && (
                            <Card>
                                <CardHeader gradient>
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                            <Braces size={18} className="text-blue-500" /> Request Body
                                        </h3>
                                        <button onClick={() => handleCopy(JSON.stringify(log.requestBody, null, 2), 'Request body')}
                                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
                                            <Copy size={12} /> Copy
                                        </button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap bg-slate-50 rounded-xl p-4 max-h-[300px] overflow-y-auto">
                                        {JSON.stringify(log.requestBody, null, 2)}
                                    </pre>
                                </CardContent>
                            </Card>
                        )}

                        {/* Request Query */}
                        {log.requestQuery && Object.keys(log.requestQuery).length > 0 && (
                            <Card>
                                <CardHeader gradient>
                                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                        <Route size={18} className="text-emerald-500" /> Request Query Parameters
                                    </h3>
                                </CardHeader>
                                <CardContent>
                                    <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap bg-slate-50 rounded-xl p-4">
                                        {JSON.stringify(log.requestQuery, null, 2)}
                                    </pre>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* RIGHT: Metadata (1/3 width) */}
                    <div className="space-y-5">
                        {/* Request Info */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <Globe size={18} className="text-blue-500" /> Request Information
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-[10px] text-slate-400 uppercase font-bold">Method</p>
                                        <p className="text-sm font-mono font-bold text-slate-700">{log.requestMethod || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-slate-400 uppercase font-bold">Path</p>
                                        <code className="text-xs font-mono text-slate-600">{log.requestPath || 'N/A'}</code>
                                    </div>
                                    {log.requestUrl && (
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Full URL</p>
                                            <code className="text-[10px] font-mono text-slate-400 break-all">{log.requestUrl}</code>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-[10px] text-slate-400 uppercase font-bold">IP Address</p>
                                        <p className="text-sm font-mono text-slate-600">{log.ipAddress || 'N/A'}</p>
                                    </div>
                                    {log.userAgent && (
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">User Agent</p>
                                            <p className="text-xs text-slate-500 break-all">{log.userAgent}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Context */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <Building2 size={18} className="text-violet-500" /> Context
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {log.tenantName && (
                                        <div className="flex items-center gap-2">
                                            <Building2 size={14} className="text-violet-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-[10px] text-slate-400 uppercase font-bold">Tenant</p>
                                                <p className="text-sm font-medium text-slate-700">{log.tenantName}</p>
                                            </div>
                                        </div>
                                    )}
                                    {log.userName && (
                                        <div className="flex items-center gap-2">
                                            <User size={14} className="text-blue-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-[10px] text-slate-400 uppercase font-bold">User</p>
                                                <p className="text-sm font-medium text-slate-700">{log.userName}</p>
                                            </div>
                                        </div>
                                    )}
                                    {log.tenantId && (
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Tenant ID</p>
                                            <code className="text-[10px] font-mono text-slate-400">{log.tenantId}</code>
                                        </div>
                                    )}
                                    {log.userId && (
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">User ID</p>
                                            <code className="text-[10px] font-mono text-slate-400">{log.userId}</code>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Log Metadata */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <FileText size={18} className="text-slate-500" /> Log Details
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-[10px] text-slate-400 uppercase font-bold">Log ID</p>
                                        <code className="text-[10px] font-mono text-slate-400 break-all">{log.id}</code>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-slate-400 uppercase font-bold">Created At</p>
                                        <p className="text-sm text-slate-600">{formatDateTime(log.createdAt)}</p>
                                    </div>
                                    {log.durationMs !== null && (
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Duration</p>
                                            <p className="text-sm font-medium text-slate-700">{log.durationMs}ms</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}